/** Accompagnement stratégique — profil entrepreneurial, engagement, FDR, activités, bilan. */

export type AccompanimentStatus =
  | 'profiling'
  | 'ready_to_engage'
  | 'engaged'
  | 'roadmap'
  | 'activities'
  | 'evaluation'
  | 'completed';

export const ACCOMPANIMENT_STATUS_LABELS: Record<AccompanimentStatus, string> = {
  profiling: 'Profil entrepreneurial',
  ready_to_engage: 'Prêt à s’engager',
  engaged: 'Engagé — en attente de conseiller',
  roadmap: 'Feuille de route',
  activities: 'Activités d’accompagnement',
  evaluation: 'Évaluation',
  completed: 'Résultats',
};

export const ACCOMPANIMENT_STEPS: AccompanimentStatus[] = [
  'profiling',
  'ready_to_engage',
  'engaged',
  'roadmap',
  'activities',
  'evaluation',
  'completed',
];

export type EntrepreneurialProfileLevel = 'emergent' | 'structurant' | 'confirme' | 'mature';

export const PROFILE_LEVEL_LABELS: Record<EntrepreneurialProfileLevel, string> = {
  emergent: 'Débutant',
  structurant: 'Intermédiaire',
  confirme: 'Intermédiaire avancé',
  mature: 'Confirmé',
};

export type AsFormulaKey = 'connect' | 'essentiel' | 'premium';

export const AS_FORMULAS = [
  {
    key: 'connect' as const,
    label: 'AS CONNECT',
    tagline: 'Accompagnement de démarrage',
    tone: 'green' as const,
    duration: '03 mois',
    features: [
      'Profil entrepreneurial',
      "Formation d'appropriation",
      "Formation d'orientation",
      'Accès communauté',
    ],
    expectedResults: [
      'Bonne compréhension du projet',
      'Besoins du projet maîtrisés',
      "Plan d'action de maîtrise élaboré",
      'Vision structurée',
    ],
  },
  {
    key: 'essentiel' as const,
    label: 'AS ESSENTIEL',
    tagline: 'Accompagnement structurant',
    tone: 'blue' as const,
    duration: '06 mois',
    features: ['AS Connect', 'Diagnostic', 'Formations essentielles', 'Assistance technique'],
    expectedResults: [
      'Business model structuré, offre adaptée, déploiement maîtrisé',
      'Pilotage maîtrisé',
      'Premières ventes assurées',
    ],
  },
  {
    key: 'premium' as const,
    label: 'AS Premium',
    tagline: 'Accompagnement complet',
    tone: 'orange' as const,
    duration: '12 mois',
    features: ['AS Essentiel', 'Formations avancées', 'Mentorat', 'Évaluation continue'],
    expectedResults: [
      'Entreprise structurée',
      'Croissance accélérée',
      'Accès aux financements',
      'Développement du marché',
    ],
  },
];

/** Alias d’anciennes clés éventuelles vers les formules actuelles. */
const FORMULA_KEY_ALIASES: Record<string, AsFormulaKey> = {
  avance: 'premium',
  avancee: 'premium',
  expert: 'premium',
};

export type AsHubModule = 'profil' | 'engagement' | 'feuille' | 'activites' | 'evaluation';

export const AS_HUB_MODULES: {
  key: AsHubModule;
  title: string;
  description: string;
  tone: 'blue' | 'green' | 'orange' | 'navy' | 'teal';
}[] = [
  {
    key: 'profil',
    title: 'Profil Entrepreneurial',
    description: 'Évaluez vos compétences et votre potentiel.',
    tone: 'blue',
  },
  {
    key: 'engagement',
    title: 'Engagement',
    description: 'Choisissez votre formule et vos résultats attendus.',
    tone: 'green',
  },
  {
    key: 'feuille',
    title: 'Feuille de Route',
    description: 'Découvrez les étapes de votre parcours.',
    tone: 'orange',
  },
  {
    key: 'activites',
    title: 'Activités d’Accompagnement',
    description: 'Suivez vos activités et vos progrès.',
    tone: 'navy',
  },
  {
    key: 'evaluation',
    title: 'Évaluation & Résultats',
    description: 'Mesurez vos résultats et progrès.',
    tone: 'teal',
  },
];

export function formulaByKey(key: string | null | undefined) {
  if (!key) return null;
  const normalized = FORMULA_KEY_ALIASES[key] ?? (key as AsFormulaKey);
  return AS_FORMULAS.find((f) => f.key === normalized) ?? null;
}

export type LikertAnswers = Record<string, number>;
export type TextAnswers = Record<string, string>;

export interface ProfileDimension {
  key: string;
  title: string;
  description: string;
  questions: { id: string; label: string }[];
}

export const ENTREPRENEURIAL_PROFILE_DIMENSIONS: ProfileDimension[] = [
  {
    key: 'motivation',
    title: 'Motivation',
    description: 'Intention et énergie pour entreprendre',
    questions: [
      { id: 'mot_1', label: 'Je sais pourquoi je veux entreprendre (ou consolider mon activité).' },
      { id: 'mot_2', label: 'Je suis prêt(e) à consacrer du temps régulier à cet accompagnement.' },
    ],
  },
  {
    key: 'experience',
    title: 'Expérience',
    description: 'Parcours et apprentissage terrain',
    questions: [
      { id: 'exp_1', label: 'J’ai déjà une expérience professionnelle ou entrepreneuriale utile.' },
      { id: 'exp_2', label: 'Je connais mon marché ou mon public cible.' },
    ],
  },
  {
    key: 'competences',
    title: 'Compétences',
    description: 'Savoir-faire opérationnels et managériaux',
    questions: [
      { id: 'comp_1', label: 'Je maîtrise les compétences clés pour faire avancer mon projet.' },
      { id: 'comp_2', label: 'Je sais identifier ce qu’il me manque et où trouver de l’aide.' },
    ],
  },
  {
    key: 'vision',
    title: 'Vision',
    description: 'Ambition et clarté stratégique',
    questions: [
      { id: 'vis_1', label: 'J’ai une vision claire de ce que je veux construire à 12–24 mois.' },
      { id: 'vis_2', label: 'Je peux formuler des objectifs concrets et mesurables.' },
    ],
  },
  {
    key: 'ressources',
    title: 'Ressources',
    description: 'Moyens humains, financiers et réseau',
    questions: [
      { id: 'res_1', label: 'Je dispose (ou peux mobiliser) des ressources pour démarrer / accélérer.' },
      { id: 'res_2', label: 'Je peux m’appuyer sur un réseau (proches, partenaires, mentors).' },
    ],
  },
  {
    key: 'resilience',
    title: 'Résilience',
    description: 'Capacité à tenir le cap face aux obstacles',
    questions: [
      { id: 'resil_1', label: 'Face aux obstacles, je sais rebondir et ajuster mon approche.' },
      { id: 'resil_2', label: 'Je suis ouvert(e) au feedback et à me faire challenger.' },
    ],
  },
  {
    key: 'finance',
    title: 'Finance',
    description: 'Lecture financière, trésorerie et pilotage économique',
    questions: [
      { id: 'fin_1', label: 'Je comprends les enjeux financiers de mon projet (coûts, marges, trésorerie).' },
      { id: 'fin_2', label: 'Je suis capable de suivre un budget et des indicateurs clés.' },
    ],
  },
];

/** Couche synthèse maquette : 7 compétences affichées, alimentées par le Likert (likertKey). */
export interface ProfileSynthesisCompetency {
  key: string;
  title: string;
  description: string;
  likertKey: string;
}

export const PROFILE_SYNTHESIS_COMPETENCIES: ProfileSynthesisCompetency[] = [
  {
    key: 'vision_strategique',
    title: 'Vision stratégique',
    description: 'Ambition, clarté et objectifs à 12–24 mois',
    likertKey: 'vision',
  },
  {
    key: 'motivation',
    title: 'Motivation & engagement',
    description: 'Intention, énergie et disponibilité pour entreprendre',
    likertKey: 'motivation',
  },
  {
    key: 'experience',
    title: 'Expérience entrepreneuriale',
    description: 'Parcours terrain et connaissance du marché',
    likertKey: 'experience',
  },
  {
    key: 'competences',
    title: 'Compétences opérationnelles',
    description: 'Savoir-faire métier et capacité à mobiliser de l’aide',
    likertKey: 'competences',
  },
  {
    key: 'finance',
    title: 'Finance & pilotage',
    description: 'Budget, trésorerie et indicateurs de performance',
    likertKey: 'finance',
  },
  {
    key: 'reseau',
    title: 'Réseau & ressources',
    description: 'Moyens humains, financiers et partenaires mobilisables',
    likertKey: 'ressources',
  },
  {
    key: 'resilience',
    title: 'Résilience & adaptabilité',
    description: 'Tenue du cap, feedback et ajustements',
    likertKey: 'resilience',
  },
];

export const PROFILE_OPEN_QUESTIONS: { id: string; label: string; placeholder: string }[] = [
  {
    id: 'ambition',
    label: 'Quelle est votre ambition principale pour les 12 prochains mois ?',
    placeholder: 'Ex. lancer mon offre, structurer mon activité, atteindre X clients…',
  },
  {
    id: 'contrainte',
    label: 'Quel est votre principal frein aujourd’hui ?',
    placeholder: 'Ex. financement, positionnement, organisation, compétences…',
  },
  {
    id: 'attente',
    label: 'Qu’attendez-vous concrètement de l’accompagnement stratégique ?',
    placeholder: 'Ex. feuille de route, priorités, suivi régulier, décisions…',
  },
];

export type ActivityStatus = 'pending' | 'in_progress' | 'done' | 'blocked';

export const ACTIVITY_STATUS_LABELS: Record<ActivityStatus, string> = {
  pending: 'À démarrer',
  in_progress: 'En cours',
  done: 'Terminée',
  blocked: 'Bloqué',
};

export type ActivityPhaseKey = 'clarifier' | 'determiner' | 'executer' | 'evaluer';

export const ACTIVITY_PHASES: {
  key: ActivityPhaseKey;
  title: string;
  description: string;
}[] = [
  {
    key: 'clarifier',
    title: 'Clarifier',
    description: 'Comprendre le contexte, les enjeux et les attentes.',
  },
  {
    key: 'determiner',
    title: 'Déterminer les actions',
    description: 'Définir les actions à mener, les ressources et le planning.',
  },
  {
    key: 'executer',
    title: 'Exécuter',
    description: 'Mettre en œuvre les actions planifiées.',
  },
  {
    key: 'evaluer',
    title: 'Évaluer & Ajuster',
    description: 'Évaluer les résultats et ajuster si nécessaire.',
  },
];

export interface ActivityActionItem {
  id: string;
  label: string;
  done: boolean;
}

/** Statut d’une étape de la feuille de route canonique (9 étapes). */
export type RoadmapStepProgress = 'done' | 'in_progress' | 'upcoming';

export const ROADMAP_STEP_PROGRESS_LABELS: Record<RoadmapStepProgress, string> = {
  done: 'Terminé',
  in_progress: 'En cours',
  upcoming: 'À venir',
};

export const AS_ROADMAP_STEPS = [
  {
    key: 'appropriation',
    order: 1,
    title: "Formation d'appropriation",
    description: 'Appropriation du parcours et des outils ISBD.',
    tone: 'blue' as const,
  },
  {
    key: 'orientation',
    order: 2,
    title: "Formation d'orientation",
    description: 'Clarifier la trajectoire et les priorités entrepreneuriales.',
    tone: 'green' as const,
  },
  {
    key: 'diagnostic',
    order: 3,
    title: 'Diagnostic',
    description: 'Analyse approfondie de votre projet/entreprise et de son environnement.',
    tone: 'orange' as const,
  },
  {
    key: 'formations_essentielles',
    order: 4,
    title: 'Formations spécifiques essentielles',
    description: 'Renforcer les compétences structurantes du projet.',
    tone: 'purple' as const,
  },
  {
    key: 'formations_avancees',
    order: 5,
    title: 'Formations spécifiques avancées',
    description: 'Approfondir les expertises pour accélérer la croissance.',
    tone: 'sky' as const,
  },
  {
    key: 'formations_maitrise',
    order: 6,
    title: 'Formations de maîtrise',
    description: 'Consolider la maîtrise opérationnelle et stratégique.',
    tone: 'gold' as const,
  },
  {
    key: 'assistance',
    order: 7,
    title: 'Assistance technique',
    description: 'Appui technique ciblé sur les enjeux du projet.',
    tone: 'red' as const,
  },
  {
    key: 'evaluation',
    order: 8,
    title: 'Évaluation / Amélioration',
    description: 'Mesurer les résultats et ajuster le plan d’action.',
    tone: 'teal' as const,
  },
  {
    key: 'mentorat',
    order: 9,
    title: 'Mentorat',
    description: 'Accompagnement personnalisé par un mentor.',
    tone: 'olive' as const,
  },
] as const;

export type AsRoadmapStepKey = (typeof AS_ROADMAP_STEPS)[number]['key'];

/** Libellés courts pour le stepper horizontal du plan d’activités. */
export const AS_ROADMAP_STEP_SHORT_LABELS: Record<AsRoadmapStepKey, string> = {
  appropriation: "Formation d'appropriation",
  orientation: "Formation d'orientation",
  diagnostic: 'Diagnostic',
  formations_essentielles: 'Formations essentielles',
  formations_avancees: 'Formations avancées',
  formations_maitrise: 'Formations de maîtrise',
  assistance: 'Assistance technique',
  evaluation: 'Évaluation / Amélioration',
  mentorat: 'Mentorat',
};

/**
 * Étapes incluses par défaut selon la formule.
 * Le conseiller peut ensuite cocher / décocher sur la feuille de route.
 */
export const AS_FORMULA_DEFAULT_STEPS: Record<AsFormulaKey, AsRoadmapStepKey[]> = {
  connect: ['appropriation', 'orientation'],
  essentiel: ['appropriation', 'orientation', 'diagnostic', 'formations_essentielles', 'assistance'],
  premium: [
    'appropriation',
    'orientation',
    'diagnostic',
    'formations_essentielles',
    'formations_avancees',
    'formations_maitrise',
    'assistance',
    'evaluation',
    'mentorat',
  ],
};

export interface RoadmapObjective {
  id: string;
  order: number;
  step_key?: AsRoadmapStepKey | string;
  title: string;
  description: string;
  target_date: string;
  /** false = non pris en charge pour cet engagement (affiché avec croix rouge). */
  included?: boolean;
  status?: RoadmapStepProgress;
  progress?: number;
}

export interface RoadmapStepView {
  key: AsRoadmapStepKey;
  order: number;
  title: string;
  description: string;
  tone: (typeof AS_ROADMAP_STEPS)[number]['tone'];
  included: boolean;
  status: RoadmapStepProgress;
  progress: number;
  target_date: string;
  objectiveId: string;
}

export interface AccompanimentActivity {
  id: string;
  accompaniment_id: string;
  objective_id: string | null;
  title: string;
  description: string | null;
  due_date: string | null;
  start_date: string | null;
  end_date: string | null;
  expected_result: string | null;
  obtained_result: string | null;
  observations: string | null;
  progress: number;
  current_phase: ActivityPhaseKey;
  action_items: ActivityActionItem[];
  status: ActivityStatus;
  sort_order: number;
  notes: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export function activityProgressFromActions(items: ActivityActionItem[] | null | undefined): number {
  const list = items ?? [];
  if (!list.length) return 0;
  const done = list.filter((a) => a.done).length;
  return Math.round((done / list.length) * 100);
}

export function activityStatusFromProgress(progress: number, current?: ActivityStatus): ActivityStatus {
  if (progress >= 100) return 'done';
  if (progress > 0) return 'in_progress';
  if (current === 'blocked') return 'blocked';
  return 'pending';
}

export function normalizeActivity(raw: Partial<AccompanimentActivity> & { id: string }): AccompanimentActivity {
  const action_items = Array.isArray(raw.action_items) ? raw.action_items : [];
  const progress =
    typeof raw.progress === 'number' && !Number.isNaN(raw.progress)
      ? Math.max(0, Math.min(100, raw.progress))
      : activityProgressFromActions(action_items);
  const phase = ACTIVITY_PHASES.some((p) => p.key === raw.current_phase)
    ? (raw.current_phase as ActivityPhaseKey)
    : 'clarifier';
  return {
    id: raw.id,
    accompaniment_id: raw.accompaniment_id ?? '',
    objective_id: raw.objective_id ?? null,
    title: raw.title ?? '',
    description: raw.description ?? null,
    due_date: raw.due_date ?? raw.end_date ?? null,
    start_date: raw.start_date ?? null,
    end_date: raw.end_date ?? raw.due_date ?? null,
    expected_result: raw.expected_result ?? null,
    obtained_result: raw.obtained_result ?? null,
    observations: raw.observations ?? null,
    progress,
    current_phase: phase,
    action_items,
    status: (raw.status as ActivityStatus) || activityStatusFromProgress(progress),
    sort_order: Number(raw.sort_order) || 0,
    notes: raw.notes ?? null,
    completed_at: raw.completed_at ?? null,
    created_at: raw.created_at ?? '',
    updated_at: raw.updated_at ?? '',
  };
}

export interface AccompanimentEvaluation {
  progress_score: number;
  impact_score: number;
  satisfaction_score: number;
  strengths: string;
  gaps: string;
  next_steps: string;
  results_summary: string;
  completed_at?: string;
  completed_by?: string;
}

export interface StrategicAccompaniment {
  id: string;
  user_id: string;
  status: AccompanimentStatus;
  likert_answers: LikertAnswers;
  open_answers: TextAnswers;
  profile_score: number | null;
  profile_level: EntrepreneurialProfileLevel | null;
  profile_summary: string | null;
  profile_completed_at: string | null;
  engaged_at: string | null;
  engagement_ack: boolean;
  formula_key: AsFormulaKey | null;
  expected_results: string | null;
  advisor_id: string | null;
  assigned_at: string | null;
  roadmap: RoadmapObjective[];
  roadmap_validated_at: string | null;
  evaluation: AccompanimentEvaluation | null;
  created_at: string;
  updated_at: string;
}

export function emptyLikertAnswers(): LikertAnswers {
  const out: LikertAnswers = {};
  for (const dim of ENTREPRENEURIAL_PROFILE_DIMENSIONS) {
    for (const q of dim.questions) out[q.id] = 0;
  }
  return out;
}

export function emptyOpenAnswers(): TextAnswers {
  const out: TextAnswers = {};
  for (const q of PROFILE_OPEN_QUESTIONS) out[q.id] = '';
  return out;
}

export function likertComplete(answers: LikertAnswers): boolean {
  return ENTREPRENEURIAL_PROFILE_DIMENSIONS.every((d) =>
    d.questions.every((q) => {
      const v = answers[q.id];
      return typeof v === 'number' && v >= 1 && v <= 5;
    }),
  );
}

export function openAnswersComplete(answers: TextAnswers): boolean {
  return PROFILE_OPEN_QUESTIONS.every((q) => (answers[q.id] ?? '').trim().length >= 8);
}

export function computeProfileScore(answers: LikertAnswers): number {
  const ids = ENTREPRENEURIAL_PROFILE_DIMENSIONS.flatMap((d) => d.questions.map((q) => q.id));
  if (!ids.length) return 0;
  const sum = ids.reduce((acc, id) => acc + (answers[id] || 0), 0);
  const max = ids.length * 5;
  return Math.round((sum / max) * 100);
}

/** Moyenne 0–5 d’une dimension (0 si aucune réponse). */
export function dimensionAverage(answers: LikertAnswers, dimKey: string): number {
  const dim = ENTREPRENEURIAL_PROFILE_DIMENSIONS.find((d) => d.key === dimKey);
  if (!dim) return 0;
  const vals = dim.questions.map((q) => answers[q.id] || 0).filter((v) => v >= 1);
  if (!vals.length) return 0;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}

export function dimensionScores(answers: LikertAnswers): { key: string; title: string; score: number }[] {
  return ENTREPRENEURIAL_PROFILE_DIMENSIONS.map((d) => ({
    key: d.key,
    title: d.title,
    score: dimensionAverage(answers, d.key),
  }));
}

/** Score 0–5 d’une compétence synthèse (maquette), dérivé du Likert sous-jacent. */
export function synthesisCompetencyAverage(answers: LikertAnswers, synthesisKey: string): number {
  const syn = PROFILE_SYNTHESIS_COMPETENCIES.find((c) => c.key === synthesisKey);
  if (!syn) return 0;
  return dimensionAverage(answers, syn.likertKey);
}

export function synthesisCompetencyScores(
  answers: LikertAnswers,
): { key: string; title: string; score: number; likertKey: string }[] {
  return PROFILE_SYNTHESIS_COMPETENCIES.map((c) => ({
    key: c.key,
    title: c.title,
    likertKey: c.likertKey,
    score: dimensionAverage(answers, c.likertKey),
  }));
}

/** Forces / axes d’après les 7 compétences synthèse (libellés maquette). */
export function profileStrengthsAndGaps(
  answers: LikertAnswers,
  take = 2,
): { strengths: { key: string; title: string; score: number }[]; gaps: { key: string; title: string; score: number }[] } {
  const scored = synthesisCompetencyScores(answers).filter((s) => s.score > 0);
  if (!scored.length) return { strengths: [], gaps: [] };

  const byHigh = [...scored].sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
  const byLow = [...scored].sort((a, b) => a.score - b.score || a.title.localeCompare(b.title));

  let strengths = byHigh.filter((s) => s.score >= 4).slice(0, take);
  if (!strengths.length && scored.length >= 2) {
    strengths = byHigh.slice(0, Math.min(take, Math.ceil(scored.length / 2)));
  } else if (!strengths.length && scored.length === 1 && byHigh[0]!.score >= 3.5) {
    strengths = [byHigh[0]!];
  }

  const strengthKeys = new Set(strengths.map((s) => s.key));
  let gaps = byLow.filter((s) => s.score <= 3 && !strengthKeys.has(s.key)).slice(0, take);
  if (!gaps.length) {
    gaps = byLow.filter((s) => !strengthKeys.has(s.key)).slice(0, take);
  }
  return { strengths, gaps };
}

export function profileLevelFromScore(score: number): EntrepreneurialProfileLevel {
  if (score >= 80) return 'mature';
  if (score >= 60) return 'confirme';
  if (score >= 40) return 'structurant';
  return 'emergent';
}

export function buildProfileSummary(
  score: number,
  level: EntrepreneurialProfileLevel,
  open: TextAnswers,
): string {
  const label = PROFILE_LEVEL_LABELS[level];
  const ambition = (open.ambition ?? '').trim();
  const attente = (open.attente ?? '').trim();
  return [
    `Profil entrepreneurial ${label} (${score}/100).`,
    ambition ? `Ambition : ${ambition}` : null,
    attente ? `Attente d’accompagnement : ${attente}` : null,
  ]
    .filter(Boolean)
    .join(' ');
}

export function emptyEvaluation(): AccompanimentEvaluation {
  return {
    progress_score: 3,
    impact_score: 3,
    satisfaction_score: 3,
    strengths: '',
    gaps: '',
    next_steps: '',
    results_summary: '',
  };
}

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function newRoadmapObjective(order: number): RoadmapObjective {
  return {
    id: newId(),
    order,
    title: '',
    description: '',
    target_date: '',
    included: true,
    status: 'upcoming',
    progress: 0,
  };
}

export function buildDefaultRoadmap(formulaKey: string | null | undefined): RoadmapObjective[] {
  const formula = formulaByKey(formulaKey)?.key ?? null;
  const includedKeys = new Set(formula ? AS_FORMULA_DEFAULT_STEPS[formula] : []);
  return AS_ROADMAP_STEPS.map((step) => ({
    id: newId(),
    order: step.order,
    step_key: step.key,
    title: step.title,
    description: step.description,
    target_date: '',
    included: includedKeys.has(step.key),
    status: 'upcoming' as RoadmapStepProgress,
    progress: 0,
  }));
}

/** Fusionne roadmap enregistrée + catalogue 9 étapes (complète les manquants). */
export function resolveRoadmapSteps(
  roadmap: RoadmapObjective[] | null | undefined,
  formulaKey: string | null | undefined,
): RoadmapStepView[] {
  const formula = formulaByKey(formulaKey)?.key ?? null;
  const defaults = new Set(formula ? AS_FORMULA_DEFAULT_STEPS[formula] : []);
  const byKey = new Map<string, RoadmapObjective>();
  for (const o of roadmap ?? []) {
    if (o.step_key) byKey.set(String(o.step_key), o);
  }
  // Anciennes roadmaps sans step_key : tenter match par titre
  if (!byKey.size && roadmap?.length) {
    for (const o of roadmap) {
      const match = AS_ROADMAP_STEPS.find(
        (s) => s.title.toLowerCase() === (o.title || '').trim().toLowerCase(),
      );
      if (match) byKey.set(match.key, { ...o, step_key: match.key });
    }
  }

  return AS_ROADMAP_STEPS.map((step) => {
    const saved = byKey.get(step.key);
    const included = saved?.included ?? defaults.has(step.key);
    const status: RoadmapStepProgress =
      !included ? 'upcoming' : saved?.status === 'done' || saved?.status === 'in_progress' ? saved.status : 'upcoming';
    return {
      key: step.key,
      order: step.order,
      title: saved?.title || step.title,
      description: saved?.description || step.description,
      tone: step.tone,
      included,
      status: included ? status : 'upcoming',
      progress: included ? Math.max(0, Math.min(100, Number(saved?.progress) || 0)) : 0,
      target_date: saved?.target_date || '',
      objectiveId: saved?.id || '',
    };
  });
}

export function roadmapStepsToObjectives(steps: RoadmapStepView[]): RoadmapObjective[] {
  return steps.map((s) => ({
    id: s.objectiveId || newId(),
    order: s.order,
    step_key: s.key,
    title: s.title,
    description: s.description,
    target_date: s.target_date,
    included: s.included,
    status: s.status,
    progress: s.progress,
  }));
}

export function currentRoadmapStep(steps: RoadmapStepView[]): RoadmapStepView | null {
  const included = steps.filter((s) => s.included);
  return included.find((s) => s.status === 'in_progress') ?? included.find((s) => s.status === 'upcoming') ?? null;
}

export function statusIndex(status: AccompanimentStatus): number {
  return ACCOMPANIMENT_STEPS.indexOf(status);
}

/** Progression globale 0–100 pour le hub AS. */
export function computeGlobalProgress(
  status: AccompanimentStatus | null,
  activities: { status: ActivityStatus }[],
): number {
  if (!status) return 0;
  const base: Record<AccompanimentStatus, number> = {
    profiling: 8,
    ready_to_engage: 22,
    engaged: 32,
    roadmap: 42,
    activities: 48,
    evaluation: 88,
    completed: 100,
  };
  if (status === 'activities' || status === 'roadmap') {
    const total = activities.length;
    if (!total) return base[status];
    const done = activities.filter((a) => a.status === 'done').length;
    return Math.min(85, base.activities + Math.round((done / total) * 35));
  }
  return base[status];
}

export function moduleUnlocked(module: AsHubModule, status: AccompanimentStatus | null): boolean {
  // Activités et évaluation restent consultables ; le contenu affiché s’adapte au statut.
  if (module === 'activites' || module === 'evaluation') return true;
  if (!status) return module === 'profil';
  const order: AsHubModule[] = ['profil', 'engagement', 'feuille', 'activites', 'evaluation'];
  const unlockedAt: Record<AccompanimentStatus, AsHubModule> = {
    profiling: 'profil',
    ready_to_engage: 'engagement',
    engaged: 'feuille',
    roadmap: 'feuille',
    activities: 'activites',
    evaluation: 'evaluation',
    completed: 'evaluation',
  };
  const current = unlockedAt[status];
  return order.indexOf(module) <= order.indexOf(current);
}

/** Contenu opérationnel du module activités (sinon aperçu / message d’attente). */
export function activitiesContentAvailable(
  status: AccompanimentStatus | null,
  roadmapValidatedAt?: string | null,
): boolean {
  if (!status) return false;
  if (roadmapValidatedAt) return true;
  return statusIndex(status) >= statusIndex('activities');
}

/** Contenu opérationnel du module évaluation (sinon message d’attente). */
export function evaluationContentAvailable(status: AccompanimentStatus | null): boolean {
  if (!status) return false;
  return statusIndex(status) >= statusIndex('evaluation');
}

export function currentHubModule(status: AccompanimentStatus | null): AsHubModule {
  if (!status) return 'profil';
  if (status === 'profiling') return 'profil';
  if (status === 'ready_to_engage') return 'engagement';
  if (status === 'engaged' || status === 'roadmap') return 'feuille';
  if (status === 'activities') return 'activites';
  return 'evaluation';
}

export interface AsNextStep {
  module: AsHubModule;
  title: string;
  description: string;
  dueDate: string | null;
  progress: number;
}

export function resolveNextStep(
  status: AccompanimentStatus | null,
  activities: AccompanimentActivity[],
  roadmap: RoadmapObjective[],
): AsNextStep {
  if (!status || status === 'profiling') {
    return {
      module: 'profil',
      title: 'Évaluer votre profil entrepreneurial',
      description: 'Analyse de vos compétences, motivations et potentiel entrepreneurial.',
      dueDate: null,
      progress: status === 'profiling' ? 35 : 0,
    };
  }
  if (status === 'ready_to_engage') {
    return {
      module: 'engagement',
      title: 'Choisir votre formule et vous engager',
      description: 'Sélectionnez votre formule AS et formalisez votre engagement.',
      dueDate: null,
      progress: 50,
    };
  }
  if (status === 'engaged') {
    return {
      module: 'feuille',
      title: 'En attente d’assignation de votre conseiller',
      description: 'Un conseiller vous sera attribué pour co-construire votre feuille de route.',
      dueDate: null,
      progress: 20,
    };
  }
  if (status === 'roadmap') {
    const first = [...roadmap].sort((a, b) => a.order - b.order)[0];
    return {
      module: 'feuille',
      title: first?.title || 'Co-construire votre feuille de route',
      description: first?.description || 'Définir avec votre conseiller les étapes de votre parcours.',
      dueDate: first?.target_date || null,
      progress: roadmap.length ? 55 : 25,
    };
  }
  if (status === 'activities') {
    const next = activities.find((a) => a.status !== 'done') ?? activities[0];
    const done = activities.filter((a) => a.status === 'done').length;
    const pct = activities.length ? Math.round((done / activities.length) * 100) : 30;
    return {
      module: 'activites',
      title: next?.title || 'Réaliser vos activités d’accompagnement',
      description: next?.description || 'Suivez les activités prévues dans votre feuille de route.',
      dueDate: next?.due_date ?? null,
      progress: pct || 30,
    };
  }
  if (status === 'evaluation') {
    return {
      module: 'evaluation',
      title: 'Évaluer vos résultats',
      description: 'Mesurez les progrès réalisés avec votre conseiller.',
      dueDate: null,
      progress: 70,
    };
  }
  return {
    module: 'evaluation',
    title: 'Consulter vos résultats',
    description: 'Bilan final de votre accompagnement stratégique.',
    dueDate: null,
    progress: 100,
  };
}

export function daysUntilLabel(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return 'Aujourd’hui';
  if (diff === 1) return 'Dans 1 jour';
  if (diff > 1) return `Dans ${diff} jours`;
  if (diff === -1) return 'Hier';
  return `Il y a ${Math.abs(diff)} jours`;
}
